import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  useLogin,
  useRegister,
  useLogout,
  useMe,
  useRequestOTP,
  useVerifyOTP,
  useChangePassword,
} from '@/hooks/api/useAuth';
import { generatedApiClient } from '@/services/generatedApiClient';
import { User, AuthContextType } from '@/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use generated hooks
  const loginMutation = useLogin();
  const registerMutation = useRegister();
  const logoutMutation = useLogout();
  const {
    data: meData,
    isLoading: meLoading,
    error: meError,
    refetch: refetchMe,
  } = useMe({ enabled: true });
  const requestOTPMutation = useRequestOTP();
  const verifyOTPMutation = useVerifyOTP();
  const changePasswordMutation = useChangePassword();

  // Update user state when meData changes
  useEffect(() => {
    if (meData) {
      const userData: User = {
        id: '1', // Backend doesn't return ID, using placeholder
        email: meData.email || '',
        name: meData.username || '',
        level: meData.level || 0,
        token: meData.token || '',
        usage: {
          burned_tokens: 0,
          completion: 0,
          limit: 0,
          prompt: 0,
          total: 0,
        },
      };
      setUser(userData);
      localStorage.setItem('userData', JSON.stringify(userData));
    } else if (meError) {
      // Clear user data on error (like 401)
      setUser(null);
      localStorage.removeItem('userData');
    }
  }, [meData, meError]);

  // Update loading state
  useEffect(() => {
    setIsLoading(
      meLoading || loginMutation.isPending || registerMutation.isPending
    );
  }, [meLoading, loginMutation.isPending, registerMutation.isPending]);

  const login = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Attempting email login');
      await loginMutation.mutateAsync({
        email,
        password,
        // Only send email, not username
      });
      // Wait a moment for cookies to be set, then refetch user data
      setTimeout(async () => {
        await refetchMe();
      }, 200);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithUsername = async (username: string, password: string) => {
    try {
      console.log('AuthContext: Attempting username login');
      await loginMutation.mutateAsync({
        username,
        password,
        // Only send username, not email
      });
      // Wait a moment for cookies to be set, then refetch user data
      setTimeout(async () => {
        await refetchMe();
      }, 200);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signup = async (email: string, _password: string, name: string) => {
    try {
      await registerMutation.mutateAsync({
        username: name,
        email,
        password: '',
        password_confirm: '',
      });
    } catch (error: unknown) {
      console.error('Signup error:', error);

      // Handle specific database constraint errors
      if (error instanceof Error) {
        if (
          error.message?.includes(
            'duplicate key value violates unique constraint'
          )
        ) {
          if (error.message.includes('idx_unique_username')) {
            throw new Error('This username is already taken');
          } else if (error.message.includes('idx_unique_email')) {
            throw new Error('This email is already registered');
          }
        }
      }

      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('userData');
    }
  };

  const requestOTP = async (
    email?: string,
    username?: string,
    action?: string
  ) => {
    try {
      await requestOTPMutation.mutateAsync({ email, username, action });
    } catch (error) {
      console.error('OTP request error:', error);
      throw error;
    }
  };

  const verifyOTP = async (
    otp: string,
    action: string,
    email?: string,
    username?: string
  ): Promise<string> => {
    try {
      const result = await verifyOTPMutation.mutateAsync({
        otp,
        action,
        email,
        username,
      } as any);
      if (result.data?.action_token) {
        return result.data.action_token;
      }
      throw new Error('No action token received');
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    }
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string,
    confirmPassword: string,
    actionToken?: string
  ) => {
    try {
      if (actionToken) {
        const resetResult = await generatedApiClient.confirmPasswordReset({
          action_token: actionToken,
          password: newPassword,
          password_confirm: confirmPassword,
        });
        if (resetResult.error) {
          throw new Error(resetResult.error);
        }
        return;
      }

      await changePasswordMutation.mutateAsync({
        password: newPassword,
        password_confirm: confirmPassword,
        old_password: oldPassword,
      });
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      await refetchMe();
    } catch (error: unknown) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithUsername,
    logout,
    signup,
    requestOTP,
    verifyOTP,
    changePassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
